import { useEffect, useState } from "react";
import { Eye, EyeOff, Video, Sparkles, Mail, Lock, User, ArrowLeft, Send } from "lucide-react";
import apiClient from '../ApiClient/ApiClient';
import axios from "axios";
import { useNavigate } from "react-router";

const Auth = () => {
  
  const navigate = useNavigate()
  const [mode, setMode] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [success, setSuccess] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isForgotPasswordTransitioning, setIsForgotPasswordTransitioning] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    forgotPasswordEmail: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    setShowError(false); // Hide error immediately
    setSuccess(""); // Clear previous success messages
    setShowSuccess(false); // Hide success immediately
    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (mode === "register") {
        // Validate password strength before registering
        if (formData.password && passwordStrength.score < 3) {
            setError("Password is too weak. Please make sure the password meets security criteria.");
            setIsLoading(false);
            return;
        }
        register()
        setMode("login");
        setFormData({ first_name: "", last_name: "", email: formData.email, password: "" });
        setPasswordStrength({ score: 0, feedback: [] });
        setIsLoading(false);
    } else {
        login()
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error and success messages when user starts typing
    if (e.target.name === 'forgotPasswordEmail') {
        setError("");
        setForgotPasswordSuccess("");
    }
  };

  const handleModeSwitch = () => {
    setIsTransitioning(true);
    setTimeout(() => {
        setMode(mode === "register" ? "login" : "register");
        setPasswordStrength({ score: 0, feedback: [] }); // Reset password strength when switching modes
        setTimeout(() => {
            setIsTransitioning(false);
        }, 50);
    }, 500);
  };

  const handleForgotPasswordClick = () => {
    setIsForgotPasswordTransitioning(true);
    setTimeout(() => {
        setShowForgotPassword(true);
        setTimeout(() => {
            setIsForgotPasswordTransitioning(false);
        }, 50);
    }, 500);
  };

  const handleBackToLogin = () => {
    setIsForgotPasswordTransitioning(true);
    setTimeout(() => {
        setShowForgotPassword(false);
        setError(""); // Clear errors
        setForgotPasswordSuccess(""); // Clear success message
        setFormData({ ...formData, forgotPasswordEmail: "" }); // Clear email field
        setTimeout(() => {
            setIsForgotPasswordTransitioning(false);
        }, 50);
    }, 500);
  };

  const handleForgotPasswordSubmit = (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    setShowError(false); // Hide error immediately
    setForgotPasswordSuccess(""); // Clear previous success messages
    
    // Validate email
    if (!formData.forgotPasswordEmail) {
        setError("Please enter your email address.");
        return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.forgotPasswordEmail)) {
        setError("Please enter a valid email address.");
        return;
    }

    setIsForgotPasswordLoading(true);

    let data = new FormData();
    data.append('email', formData.forgotPasswordEmail);

    let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/password/forgot`,
        data : data
    };

    apiClient.request(config)
    .then((response) => {
        console.log(JSON.stringify(response.data));
        setForgotPasswordSuccess("Password reset link has been sent to your email. Please check your inbox.");
        setError(""); // Clear any previous errors
        setIsForgotPasswordLoading(false);
        // Clear email field after successful submission
        setFormData({ ...formData, forgotPasswordEmail: "" });
    })
    .catch((error) => {
        console.log(error);
        setIsForgotPasswordLoading(false);
        
        // Helper function to extract error message safely
        const getErrorMessage = (errorData) => {
          if (!errorData) return null;
          
          // If message is a string, return it
          if (typeof errorData.message === 'string') {
            return errorData.message;
          }
          
          // If message is an object, try to extract meaningful text
          if (typeof errorData.message === 'object' && errorData.message !== null) {
            // Try common error message formats
            if (Array.isArray(errorData.message)) {
              return errorData.message.join(', ');
            }
            // If it's an object, try to get the first value or stringify
            const firstValue = Object.values(errorData.message)[0];
            if (typeof firstValue === 'string') {
              return firstValue;
            }
            if (Array.isArray(firstValue)) {
              return firstValue.join(', ');
            }
          }
          
          // Try error field
          if (typeof errorData.error === 'string') {
            return errorData.error;
          }
          
          return null;
        };
        
        // Handle different error scenarios
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const errorData = error.response.data;
          const errorMessage = getErrorMessage(errorData);
          
          if (status === 404) {
            setError("Email address not found. Please check your email and try again.");
          } else if (status === 422) {
            setError(errorMessage || "Invalid email address. Please check your email format and try again.");
          } else if (status === 429) {
            setError("Too many requests. Please wait a moment before trying again.");
          } else if (status >= 500) {
            setError("Server error occurred. Please try again later.");
          } else {
            setError(errorMessage || "Failed to send reset link. Please try again.");
          }
        } else if (error.request) {
          // Request was made but no response received
          setError("Unable to connect to the server. Please check your internet connection and try again.");
        } else {
          // Something else happened
          setError("An unexpected error occurred. Please try again.");
        }
    });
  };

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    if (!password) {
        return { score: 0, feedback: [] };
    }

    let score = 0;
    const feedback = [];

    // Length check
    if (password.length >= 8) {
        score += 1;
    } else {
        feedback.push("Minimum 8 characters");
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
        score += 1;
    } else {
        feedback.push("Use lowercase letters");
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
        score += 1;
    } else {
        feedback.push("Use uppercase letters");
    }

    // Number check
    if (/\d/.test(password)) {
        score += 1;
    } else {
        feedback.push("Use numbers");
    }

    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        score += 1;
    } else {
        feedback.push("Use special characters");
    }

    return { score, feedback };
  };

  const getStrengthColor = (score) => {
    if (score === 0) return "bg-gray-500";
    if (score <= 2) return "bg-red-500";
    if (score <= 3) return "bg-yellow-500";
    if (score <= 4) return "bg-blue-500";
    return "bg-green-500";
  };

  const getStrengthLabel = (score) => {
    if (score === 0) return "Empty";
    if (score <= 2) return "Weak";
    if (score <= 3) return "Medium";
    if (score <= 4) return "Strong";
    return "Very Strong";
  };

  const register = () => {
    let data = new FormData();
    data.append('first_name', formData.first_name);
    data.append('last_name', formData.last_name);
    data.append('email', formData.email);
    data.append('password', formData.password);

    let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/register`,
        data : data
    };

    axios.request(config)
    .then((response) => {
        console.log(response);
        setSuccess("Registration successful! Please wait for our approval, which may take up to 2 days (48 hours).");
        setError(""); // Clear any previous errors
        setIsLoading(false);
    })
    .catch((error) => {
        console.log(error);
        setIsLoading(false);
        
        // Helper function to extract error message safely
        const getErrorMessage = (errorData) => {
          if (!errorData) return null;
          
          // If message is a string, return it
          if (typeof errorData.message === 'string') {
            return errorData.message;
          }
          
          // If message is an object, try to extract meaningful text
          if (typeof errorData.message === 'object' && errorData.message !== null) {
            // Try common error message formats
            if (Array.isArray(errorData.message)) {
              return errorData.message.join(', ');
            }
            // If it's an object, try to get the first value or stringify
            const firstValue = Object.values(errorData.message)[0];
            if (typeof firstValue === 'string') {
              return firstValue;
            }
            if (Array.isArray(firstValue)) {
              return firstValue.join(', ');
            }
          }
          
          // Try error field
          if (typeof errorData.error === 'string') {
            return errorData.error;
          }
          
          return null;
        };
        
        // Handle different error scenarios
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const errorData = error.response.data;
          const errorMessage = getErrorMessage(errorData);
          
          if (status === 400 || status === 422) {
            setError(errorMessage || "Invalid input data. Please check your information and try again.");
          } else if (status === 409) {
            setError("Email already registered. Please use a different email or login.");
          } else if (status >= 500) {
            setError("Server error occurred. Please try again later.");
          } else {
            setError(errorMessage || "Registration failed. Please try again.");
          }
        } else if (error.request) {
          // Request was made but no response received
          setError("Unable to connect to the server. Please check your internet connection and try again.");
        } else {
          // Something else happened
          setError("An unexpected error occurred. Please try again.");
        }
    });

  }

  const login = () => {
    let data = new FormData();
    data.append('email', formData.email);
    data.append('password', formData.password);

    let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/login`,
        data : data,
        withCredentials: true,
    };

    axios.request(config)
    .then((response) => {
        console.log(response)
        localStorage.setItem("token", response.data.results.access_token)
        setError(""); // Clear any previous errors
        setIsLoading(false);
        navigate("/");
    })
    .catch((error) => {
        console.log(error);
        setIsLoading(false);
        
        // Helper function to extract error message safely
        const getErrorMessage = (errorData) => {
          if (!errorData) return null;
          
          // If message is a string, return it
          if (typeof errorData.message === 'string') {
            return errorData.message;
          }
          
          // If message is an object, try to extract meaningful text
          if (typeof errorData.message === 'object' && errorData.message !== null) {
            // Try common error message formats
            if (Array.isArray(errorData.message)) {
              return errorData.message.join(', ');
            }
            // If it's an object, try to get the first value or stringify
            const firstValue = Object.values(errorData.message)[0];
            if (typeof firstValue === 'string') {
              return firstValue;
            }
            if (Array.isArray(firstValue)) {
              return firstValue.join(', ');
            }
          }
          
          // Try error field
          if (typeof errorData.error === 'string') {
            return errorData.error;
          }
          
          return null;
        };
        
        // Handle different error scenarios
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const errorData = error.response.data;
          const errorMessage = getErrorMessage(errorData);
          
          if (status === 401) {
            setError("Invalid email or password. Please check your credentials and try again.");
          } else if (status === 422) {
            setError("Invalid input data. Please check your email and password format.");
          } else if (status === 404) {
            setError("Login endpoint not found. Please contact the administrator.");
          } else if (status >= 500) {
            setError("Server error occurred. Please try again later or contact support.");
          } else {
            setError("Login failed. Please try again or contact support if the problem persists.");
          }
        } else if (error.request) {
          // Request was made but no response received
          setError("Unable to connect to the server. Please check your internet connection and try again.");
        } else {
          // Something else happened
          setError("An unexpected error occurred. Please try again or contact support.");
        }
    });
  }

  const checkCredit = () => {
      let config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_API_URL}/quota/get`,
      };

      apiClient.request(config)
      .then((response) => {
          // if(response.data.current_quota < 10000000) {
          //     setQuota(formatAngka(response.data.current_quota));
          // } else {
          //     setQuota(formatAngka(999999999));
          // }
          // setMuncul(true)
          navigate("/");
          // console.log(response);
      })
      .catch((error) => {
          console.log(error);
          // navigate("/auth");
      });
  }

    useEffect(() => {
        checkCredit();
    }, [])

    // Auto-hide error after 5 seconds
    useEffect(() => {
        if (error) {
            setShowError(true);
            const timer = setTimeout(() => {
                setShowError(false);
                // Clear error message after fade out animation completes
                setTimeout(() => {
                    setError("");
                }, 300); // Wait for transition to complete
            }, 5000); // Show error for 5 seconds

            return () => clearTimeout(timer);
        }
    }, [error]);

    // Auto-hide success after 5 seconds
    useEffect(() => {
        if (success) {
            setShowSuccess(true);
            const timer = setTimeout(() => {
                setShowSuccess(false);
                // Clear success message after fade out animation completes
                setTimeout(() => {
                    setSuccess("");
                }, 300); // Wait for transition to complete
            }, 5000); // Show success for 5 seconds

            return () => clearTimeout(timer);
        }
    }, [success]);

    // Calculate password strength when password changes (only in register mode)
    useEffect(() => {
        if (mode === "register" && formData.password) {
            const strength = calculatePasswordStrength(formData.password);
            setPasswordStrength(strength);
        } else {
            setPasswordStrength({ score: 0, feedback: [] });
        }
    }, [formData.password, mode]);

    // Auto-hide forgot password success after 8 seconds
    useEffect(() => {
        if (forgotPasswordSuccess) {
            const timer = setTimeout(() => {
                setForgotPasswordSuccess("");
            }, 8000); // Show success for 8 seconds

            return () => clearTimeout(timer);
        }
    }, [forgotPasswordSuccess]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">

        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        {/* Auth Card */}
        <div className="w-full max-w-md relative z-10 animate-slide-up" style={{ perspective: '1000px' }}>
            {/* Logo */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 mb-4 glow-primary transition-transform duration-300 hover:scale-110">
                    <Video className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground transition-all duration-300">Video Editor Pro</h1>
                <p className={`text-muted-foreground mt-1 transition-all duration-500 ease-in-out ${
                    (isTransitioning || isForgotPasswordTransitioning) ? 'opacity-0 translate-y-3 scale-95' : 'opacity-100 translate-y-0 scale-100'
                }`}>
                    {showForgotPassword ? "Reset Your Password" : mode === "register" ? "Create New Account" : "Log In to Your Account"}
                </p>
            </div>

            {/* Card */}
            <div  
                className={`glass-card rounded-2xl p-8 shadow-2xl transition-all duration-500 ease-in-out ${
                    (isTransitioning || isForgotPasswordTransitioning) ? 'opacity-95' : 'opacity-100'
                }`}
                style={{
                    transform: (isTransitioning || isForgotPasswordTransitioning) ? 'scale(0.98) rotateY(2deg)' : 'scale(1) rotateY(0deg)',
                    transformStyle: 'preserve-3d',
                    transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
            {/* Forgot Password Form */}
            {showForgotPassword ? (
                <form onSubmit={handleForgotPasswordSubmit} className={`space-y-5 transition-all duration-500 ease-in-out ${isForgotPasswordTransitioning ? 'opacity-0 translate-x-6 scale-[0.96] blur-sm' : 'opacity-100 translate-x-0 scale-100 blur-0'}`}>
                {/* Back Button */}
                <button
                    type="button"
                    onClick={handleBackToLogin}
                    disabled={isForgotPasswordTransitioning}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-all duration-300 mb-4 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
                    <span className="text-sm font-medium">Back to Login</span>
                </button>

                {/* Description */}
                <div className="mb-6">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                    Enter your email address and we'll send you a link to reset your password.
                    </p>
                </div>

                {/* Email Input */}
                <div className="space-y-2 transition-all duration-300">
                    <label className="text-sm font-medium text-foreground">Email Address</label>
                    <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-all duration-300" />
                    <input
                        name="forgotPasswordEmail"
                        type="email"
                        placeholder="your_email@email.com"
                        value={formData.forgotPasswordEmail}
                        onChange={handleInputChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 transition-all duration-300"
                        required
                    />
                    </div>
                </div>

                {/* Success Message */}
                {forgotPasswordSuccess && (
                    <div className={`rounded-md bg-green-500/15 border border-green-500/40 p-3 backdrop-blur-sm transition-all duration-300 ease-in-out animate-slideDownFade`} 
                    >
                        <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 mt-0.5">
                                <svg className="w-4 h-4 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p className="text-sm text-green-300 font-medium flex-1">{forgotPasswordSuccess}</p>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className={`rounded-md bg-red-500/15 border border-red-500/40 p-3 backdrop-blur-sm transition-all duration-300 ease-in-out ${
                        showError 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                    >
                        <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 mt-0.5">
                                <svg className="w-4 h-4 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p className="text-sm text-red-300 font-medium flex-1">{error}</p>
                        </div>
                    </div>
                )}

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isForgotPasswordTransitioning || isForgotPasswordLoading}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground font-semibold shadow-lg glow-primary hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.95] active:shadow-md touch-manipulation select-none py-3 h-12 w-full rounded-lg px-8 mt-6 relative overflow-hidden"
                >
                    {isForgotPasswordLoading ? (
                        <div className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-[3px] border-primary-foreground/20 border-t-primary-foreground border-r-primary-foreground rounded-full animate-spin" />
                            <span className="text-sm font-medium animate-pulse">Sending...</span>
                        </div>
                    ) : (
                        <span className={`transition-all duration-500 ease-in-out ${isForgotPasswordTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                            <Send className="w-5 h-5 inline-block mr-2 transition-transform duration-500" />
                            Send Reset Link
                        </span>
                    )}
                    {/* Ripple effect overlay for mobile */}
                    {!isForgotPasswordLoading && (
                        <span className="absolute inset-0 rounded-lg bg-white/0 active:bg-white/10 transition-colors duration-150 pointer-events-none" />
                    )}
                </button>
                </form>
            ) : (
                <form onSubmit={handleSubmit} className={`space-y-5 transition-all duration-500 ease-in-out ${
                isTransitioning ? 'opacity-0 translate-x-6 scale-[0.96] blur-sm' : 'opacity-100 translate-x-0 scale-100 blur-0'
                }`}>
                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
                mode === "register" 
                    ? (isTransitioning ? 'max-h-0 opacity-0 -translate-y-6 scale-95' : 'max-h-96 opacity-100 translate-y-0 scale-100')
                    : 'max-h-0 opacity-0 -translate-y-6 scale-95'
                }`}>
                    <div className="space-y-5">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">First Name</label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-colors duration-300" />
                                <input
                                name="first_name"
                                type="text"
                                placeholder="Enter your first name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                required={mode === "register"}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 transition-all duration-300"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Last Name</label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-colors duration-300" />
                                <input
                                name="last_name"
                                type="text"
                                placeholder="Enter your last name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                required={mode === "register"}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 transition-all duration-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-2 transition-all duration-300">
                    <label className="text-sm font-medium text-foreground">Email</label>
                    <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-all duration-300" />
                        <input
                        name="email"
                        type="email"
                        placeholder="your_email@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 transition-all duration-300"
                        required
                        />
                    </div>
                </div>

                <div className="space-y-2 transition-all duration-300">
                <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">Password</label>
                    {mode === "login" && (
                    <button
                        type="button"
                        onClick={handleForgotPasswordClick}
                        disabled={isForgotPasswordTransitioning}
                        className="text-xs text-primary font-medium hover:underline transition-all duration-300 hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Forgot Password?
                    </button>
                    )}
                </div>
                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-all duration-300" />
                    <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 transition-all duration-300"
                    required
                    minLength={mode === "register" ? 8 : 3}
                    />
                    <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-all duration-300 hover:scale-110"
                    >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                </div>

                {/* Password Strength Indicator - Only show in register mode */}
                {mode === "register" && formData.password && (
                    <div className="space-y-2 mt-3 animate-slideDownFade">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Password Strength:</span>
                        <span
                        className={`font-medium ${
                            passwordStrength.score <= 2
                            ? "text-red-400"
                            : passwordStrength.score <= 3
                            ? "text-yellow-400"
                            : passwordStrength.score <= 4
                            ? "text-blue-400"
                            : "text-green-400"
                        }`}
                        >
                        {getStrengthLabel(passwordStrength.score)}
                        </span>
                    </div>
                    <div className="flex gap-1 h-1.5 bg-black/30 rounded-full overflow-hidden">
                        {[1, 2, 3, 4, 5].map((level) => (
                        <div
                            key={level}
                            className={`flex-1 transition-all duration-500 ${
                            level <= passwordStrength.score
                                ? getStrengthColor(passwordStrength.score)
                                : "bg-gray-600/30"
                            }`}
                            style={{
                            transform: level <= passwordStrength.score ? "scaleY(1)" : "scaleY(0.5)",
                            }}
                        />
                        ))}
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                        {passwordStrength.feedback.map((item, index) => (
                            <span
                            key={index}
                            className="text-xs px-2 py-1 rounded-md bg-red-500/10 border border-red-500/20 text-red-300"
                            >
                            {item}
                            </span>
                        ))}
                        </div>
                    )}
                    </div>
                )}
                </div>

                {/* Success Message */}
                {success && (
                <div 
                    className={`rounded-md bg-green-500/15 border border-green-500/40 p-3 backdrop-blur-sm transition-all duration-300 ease-in-out ${
                    showSuccess 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                >
                    <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <p className="text-sm text-green-300 font-medium flex-1">{success}</p>
                    </div>
                </div>
                )}

                {/* Error Message */}
                {error && (
                <div 
                    className={`rounded-md bg-red-500/15 border border-red-500/40 p-3 backdrop-blur-sm transition-all duration-300 ease-in-out ${
                    showError 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                >
                    <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <p className="text-sm text-red-300 font-medium flex-1">{error}</p>
                    </div>
                </div>
                )}

                <button
                    type="submit"
                    variant="auth"
                    size="full"
                    disabled={isLoading || isTransitioning}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground font-semibold shadow-lg glow-primary hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.95] active:shadow-md touch-manipulation select-none py-3 h-12 w-full rounded-lg px-8 mt-6 relative overflow-hidden"
                >
                    {isLoading ? (
                    <div className="flex items-center justify-center gap-3">
                        <div className="w-6 h-6 border-[3px] border-primary-foreground/20 border-t-primary-foreground border-r-primary-foreground rounded-full animate-spin" />
                        <span className="text-sm font-medium animate-pulse">
                        {mode === "register" ? "Creating account..." : "Logging in..."}
                        </span>
                    </div>
                    ) : (
                    <span className={`transition-all duration-500 ease-in-out ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                        <Sparkles className="w-5 h-5 inline-block mr-2 transition-transform duration-500" />
                        {mode === "register" ? "Sign Up Now" : "Log In"}
                    </span>
                    )}
                    {/* Ripple effect overlay for mobile */}
                    {!isLoading && (
                    <span className="absolute inset-0 rounded-lg bg-white/0 active:bg-white/10 transition-colors duration-150 pointer-events-none" />
                    )}
                </button>
                </form>
            )}

            {/* Switch Mode - Only show when not in Forgot Password */}
            {!showForgotPassword && (
                <div className={`mt-6 text-center transition-all duration-500 ease-in-out ${
                isTransitioning ? 'opacity-0 translate-y-3 scale-95' : 'opacity-100 translate-y-0 scale-100'
                }`}>
                <p className="text-muted-foreground">
                    {mode === "register" ? "Already have an account?" : "Don't have an account?"}{" "}
                    <button
                    type="button"
                    onClick={handleModeSwitch}
                    disabled={isTransitioning}
                    className="text-primary font-medium hover:underline transition-all duration-300 hover:text-primary/80 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed relative group"
                    >
                    <span className="relative z-10">{mode === "register" ? "Log In" : "Sign Up"}</span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                    </button>
                </p>
                </div>
            )}
            </div>

            {/* Footer */}
            <p className="text-center text-sm text-muted-foreground mt-6">
            By signing up, you agree to our Terms of Service and Privacy Policy.
            </p>
        </div>
    </div>
  );
};

export default Auth;
